import React from "react";
import { Link } from "react-router-dom";
import MarketingPageLayout, { PolicySection } from "@/components/marketing/MarketingPageLayout";

/**
 * /privacy — Sample privacy policy. NOT legal advice. Hotels deploying
 * GuestIntel should adapt this to their jurisdiction.
 */
export default function PrivacyPolicy() {
  return (
    <MarketingPageLayout
      eyebrow="Policy"
      title="Privacy Policy"
      subtitle="How GuestIntel collects, uses, protects, and shares personal data — and the rights you have over it."
      lastUpdated="February 2026"
      path="/privacy"
      seoDescription="GuestIntel Privacy Policy — how we collect, process, encrypt, and share personal data, and the rights you have under GDPR and similar regimes."
    >
      <p className="italic text-sm text-zinc-500">
        This page is sample content provided for product preview purposes. It is not a substitute for
        legal advice. Hotels and lodging providers integrating GuestIntel should consult their counsel and
        publish a privacy policy that reflects their jurisdiction.
      </p>

      <PolicySection title="1. Who we are">
        <p>
          GuestIntel ("we", "us", "our") operates a hospitality incident intelligence network used by
          participating properties to document, share, and search verified incident records. This policy
          describes how we handle personal data submitted to, processed by, and surfaced through the
          network.
        </p>
      </PolicySection>

      <PolicySection title="2. The personal data we process">
        <ul>
          <li><strong>Operator data:</strong> account credentials, role, organisation, contact details, IP address.</li>
          <li><strong>Incident data submitted by participating properties:</strong> guest name and aliases, contact details, government-issued ID type and number, nationality, date of birth, incident category, description, dates, damage costs, evidence photos and documents.</li>
          <li><strong>Audit data:</strong> who accessed which record and why, including the typed reason supplied during a PII reveal.</li>
          <li><strong>Network analytics:</strong> aggregated, anonymised statistics about volume, severity, and category.</li>
        </ul>
      </PolicySection>

      <PolicySection title="3. How we use it">
        <ul>
          <li>To provide the documented incident search service to participating properties.</li>
          <li>To verify identities through Intel Shield™ matching <em>without</em> revealing direct identifiers to non-owning properties.</li>
          <li>To investigate appeals and disputes.</li>
          <li>To meet legal, regulatory, and audit obligations.</li>
          <li>To improve product safety, fairness, and accuracy.</li>
        </ul>
      </PolicySection>

      <PolicySection title="4. Legal bases (GDPR)">
        <ul>
          <li><strong>Legitimate interests</strong> in preventing fraud, property damage, and safety incidents across the hospitality network.</li>
          <li><strong>Contractual necessity</strong> with the operating property that submits a record.</li>
          <li><strong>Consent</strong> where required for analytics cookies and optional communications.</li>
          <li><strong>Legal obligations</strong> for retention, disclosure, and breach notification.</li>
        </ul>
      </PolicySection>

      <PolicySection title="5. How we protect personal data">
        <ul>
          <li><strong>Encryption at rest</strong> for direct identifiers (email, phone, ID number) using AES-256 (Fernet).</li>
          <li><strong>Encryption in transit</strong> with TLS 1.2+ everywhere.</li>
          <li><strong>Mandatory MFA</strong> (TOTP or passkey) for super admins, company admins, and case managers.</li>
          <li><strong>Intel Shield™ matching</strong> — cross-tenant matches return verdicts only, never raw identifiers.</li>
          <li><strong>Audit trails</strong> on every PII view, every reveal, and every Intel Shield match (logged with the reason).</li>
        </ul>
      </PolicySection>

      <PolicySection title="6. Sharing">
        <p>
          We do not sell personal data. We share strictly with: participating properties under their lawful
          basis, our sub-processors (cloud hosting, transactional email) under data-processing agreements,
          and regulators or law-enforcement when legally compelled. See our <Link to="/data-retention">Data Retention Policy</Link>
          {" "}for retention horizons.
        </p>
      </PolicySection>

      <PolicySection title="7. Your rights">
        <p>You can exercise any of these rights via the <Link to="/gdpr">GDPR Rights portal</Link>:</p>
        <ul>
          <li>Right of access (subject access request).</li>
          <li>Right to rectification — correct inaccurate data.</li>
          <li>Right to erasure / anonymisation — see <Link to="/data-retention">Data Retention</Link>.</li>
          <li>Right to restrict processing — most often via the <Link to="/appeal">Appeal portal</Link>.</li>
          <li>Right to portability.</li>
          <li>Right to object — lodge a complaint with your supervisory authority.</li>
        </ul>
      </PolicySection>

      <PolicySection title="8. Contact">
        <p>
          Data Protection enquiries: <a href="mailto:trust@guestintel.net">trust@guestintel.net</a>.<br />
          DPO: GuestIntel Trust Team — see <Link to="/trust">Trust Center</Link>.
        </p>
      </PolicySection>
    </MarketingPageLayout>
  );
}
